/**
 * CSS Manager for Shell SDK
 * Handles font loading and shadow DOM injection with runtime-fetched CSS
 */

import { ICSSManager } from "@mf-tools/cli-common";
import { logger } from "@mf-tools/cli-common";

const log = logger.browser('css-manager');

export interface CSSManagerConfig {
  fontsUrl?: string;
  /**
   * URL to fetch design system CSS from at runtime.
   * REQUIRED - CSS is always fetched from this URL.
   *
   * Benefits:
   * - Smaller JS bundle (no CSS embedded)
   * - Runtime theme updates (deploy new CSS without rebuilding apps)
   * - Version control via URL
   *
   * Example: 'https://cdn.yourapp.com/design-system/v1/index.css'
   * Dev: 'http://localhost:7100/mf-sdk.css'
   */
  cssUrl: string;
}

export class CSSManager implements ICSSManager {
  private config: CSSManagerConfig;
  private cachedCSS: string | null = null;

  constructor(config: CSSManagerConfig) {
    this.config = {
      fontsUrl: "https://fonts.googleapis.com/css2?family=Mulish:wght@400;700&family=Merriweather:wght@400;700&display=swap",
      ...config
    };
  }

  /**
   * Load Google Fonts and set CSS variables on document root
   */
  async loadFonts(): Promise<void> {
    // Create and inject font link element
    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = this.config.fontsUrl!;
    fontLink.crossOrigin = 'anonymous';

    // Add to document head
    document.head.appendChild(fontLink);

    // Wait for fonts to load
    return new Promise((resolve, reject) => {
      fontLink.onload = () => {
        log.info('Fonts loaded successfully');
        this.setCSSVariables();
        resolve();
      };
      fontLink.onerror = () => {
        log.error('Failed to load fonts');
        reject(new Error('Font loading failed'));
      };
    });
  }

  /**
   * Set CSS variables on document root for theme inheritance
   */
  private setCSSVariables(): void {
    const root = document.documentElement;

    // Font variables (will inherit into shadow DOM)
    root.style.setProperty('--mf-font-family', "'Mulish', ui-sans-serif, system-ui, sans-serif");
    root.style.setProperty('--mf-font-serif', "'Merriweather', serif");

    // Theme variables will be set by design system runtime
    log.info('CSS variables set on document root');
  }

  /**
   * Get design system CSS from runtime fetch
   */
  async getBundledDesignSystemCSS(forceReload = false): Promise<string> {
    // Return cached if available (unless force reload)
    if (this.cachedCSS && !forceReload) {
      return this.cachedCSS;
    }

    // Fetch CSS from configured URL with cache busting in dev
    try {
      const url = forceReload
        ? `${this.config.cssUrl}?t=${Date.now()}`
        : this.config.cssUrl;

      log.info(`Fetching design system CSS from: ${url}`);
      const response = await fetch(url, {
        cache: forceReload ? 'no-cache' : 'default'
      });

      if (!response.ok) {
        throw new Error(`Failed to load CSS: HTTP ${response.status} ${response.statusText}`);
      }
      this.cachedCSS = await response.text();
      log.info('Design system CSS loaded successfully');
      return this.cachedCSS;
    } catch (error) {
      log.error(`Failed to fetch CSS from ${this.config.cssUrl}:`, error);
      throw new Error(`Cannot load design system CSS from ${this.config.cssUrl}. Ensure the CSS file is accessible.`);
    }
  }

  /**
   * Inject CSS into a shadow root
   */
  injectCSS(shadowRoot: ShadowRoot, css?: string): void {
    if (!this.cachedCSS) {
      throw new Error('CSS not loaded. Call getBundledDesignSystemCSS() first.');
    }
    const cssToInject = this.cachedCSS + (css ?? '');

    // Convert :root selectors to :host for shadow DOM compatibility
    const shadowCompatibleCSS = cssToInject
      .replace(/:root\s*\{/g, ':host {')
      .replace(/html,\s*:host\s*\{/g, ':host {')
      .replace(/html\s*\{/g, ':host {');

    // Create style element
    const styleElement = document.createElement('style');
    styleElement.textContent = shadowCompatibleCSS;

    // Inject at the beginning of shadow root
    shadowRoot.insertBefore(styleElement, shadowRoot.firstChild);

    log.info('CSS injected into shadow DOM with :host selectors');
  }

  /**
   * Get cached CSS (useful for debugging)
   */
  getCachedCSS(): string | null {
    return this.cachedCSS;
  }
}